/* eslint-disable react/prop-types */
import { Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';
import { type EventApplicationEntrypointSpecsFragment } from '../../../../../api/fragments/EventApplicationEntrypointSpecs';
import {
    CreateEventApplicationEntrypointDocument,
    type CreateEventApplicationEntrypointMutation,
    type CreateEventApplicationEntrypointMutationVariables,
} from '../../../../../api/mutations/createEventApplicationEntrypoint';
import {
    UpdateEventApplicationEntrypointDocument,
    type UpdateEventApplicationEntrypointMutation,
    type UpdateEventApplicationEntrypointMutationVariables,
} from '../../../../../api/mutations/updateEventApplicationEntrypoint';
import { type EventApplicationEntrypointSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import PathScriptField from '../../PathScriptField';
import { type EndpointImplementation } from './types';

const implementation: EndpointImplementation<
    EventApplicationEntrypointSpecsFragment,
    EventApplicationEntrypointSettings & { eventApplicationModuleId?: string }
> = {
    getTitle: t => t('routerDetails:endpoints.EventApplicationEntrypoint.title'),
    ViewPanelComponent: ({ endpoint }) => (
        <Descriptions size="small" bordered>
            <Descriptions.Item label="Module">{endpoint.eventApplicationModule.displayName}</Descriptions.Item>
            <Descriptions.Item label="Display Name">{endpoint.displayName}</Descriptions.Item>
        </Descriptions>
    ),
    getIsPrivate: () => true,
    FormComponent: () => {
        const { t } = useTranslation(['routerDetails']);

        return (
            <>
                <InputField
                    {...t('routerDetails:endpoints.fields.pathname', { returnObjects: true })}
                    name="pathname"
                />
                <InputField
                    {...t('routerDetails:endpoints.fields.displayName', { returnObjects: true })}
                    name="displayName"
                />
                <PathScriptField name="pathScripts" />
            </>
        );
    },
    getFormInitialValues: endpoint => ({
        pathname: endpoint.pathname,
        displayName: endpoint.displayName,
        pathScripts: endpoint.pathScripts || [],
    }),
    submitCreation: (apolloClient, routerId, { eventApplicationModuleId, pathScripts, ...values }) => {
        if (!eventApplicationModuleId) {
            throw new Error('eventApplicationModuleId is required');
        }

        return apolloClient.mutate<
            CreateEventApplicationEntrypointMutation,
            CreateEventApplicationEntrypointMutationVariables
        >({
            mutation: CreateEventApplicationEntrypointDocument,
            variables: {
                routerId,
                eventApplicationModuleId,
                settings: {
                    ...values,
                    pathScripts: pathScripts || [],
                },
            },
        });
    },
    submitUpdate: (apolloClient, routerId, endpointId, values) =>
        apolloClient.mutate<
            UpdateEventApplicationEntrypointMutation,
            UpdateEventApplicationEntrypointMutationVariables
        >({
            mutation: UpdateEventApplicationEntrypointDocument,
            variables: { routerId, endpointId, settings: values },
        }),
};

export default implementation;
